/**
 * VIB-2834: varsel om ny versjon.
 *
 * Bundelen bakes med versjonen den er bygget fra (`__APP_VERSION__`), og
 * bygget skriver den samme verdien til `/version.json`. Når en ny deploy er
 * ute, svarer `/version.json` med noe annet enn det fanen har i minnet, og da
 * vet vi at fanen kjører gammel kode.
 *
 * Vi laster aldri inn på nytt automatisk. Brukeren kan ha ulagret arbeid i
 * tilbudseditoren eller timeføringen, så varselet ber om en reload og lar
 * brukeren velge når.
 *
 * Ikke hver deploy skal gi varsel (Erik, 26.10.2026). `version.json` har også
 * utrullingene fra «Nytt i Revehiet». Er det en ny utrulling brukeren får se,
 * vises varselet med en gang. Ellers vises det høyst én gang per døgn, så
 * rettinger likevel når alle innen et døgn.
 */
import type { ReleaseNote } from "@/lib/changelog/release-types";

/** Versjonen denne fanen kjører. `dev` på dev-serveren og i tester. */
export const APP_VERSION: string =
  typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "dev";

export const VERSION_URL = "/version.json";

/** Hvor ofte vi sjekker mens fanen står åpen. Fokus på fanen sjekker i tillegg. */
export const VERSION_POLL_MS = 5 * 60 * 1000;

/** Minste tid mellom to varsler når deployen ikke har en ny utrulling. */
export const DAILY_PROMPT_MS = 24 * 60 * 60 * 1000;

/** Hvor siste varsel huskes, delt mellom fanene i nettleseren. */
export const LAST_PROMPT_KEY = "revehiet:oppdateringsvarsel";

export interface DeployedVersion {
  version: string;
  /** Utrullingene i den deployede versjonen. Tom for eldre versjonsfiler. */
  releases: ReleaseNote[];
}

/**
 * Leser versjonsfila, eller null hvis svaret ikke er en versjonsfil.
 * Dev-serveren og SPA-fallbacken svarer med `index.html` på stier som ikke
 * finnes, og det skal tolkes som «vet ikke», ikke som en ny versjon.
 */
export function parseVersionResponse(body: unknown): DeployedVersion | null {
  if (typeof body !== "object" || body === null) return null;
  const { version, releases } = body as { version?: unknown; releases?: unknown };
  if (typeof version !== "string" || version.trim() === "") return null;
  return { version, releases: Array.isArray(releases) ? (releases as ReleaseNote[]) : [] };
}

/** Henter versjonen som er deployet nå. Null når den ikke kan avgjøres. */
export async function fetchDeployedVersion(
  fetchImpl: typeof fetch = fetch,
): Promise<DeployedVersion | null> {
  try {
    // Spørringsparameteren slår gjennom mellomlagre som ignorerer no-store.
    const res = await fetchImpl(`${VERSION_URL}?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    return parseVersionResponse(await res.json());
  } catch {
    return null;
  }
}

/**
 * True når en annen versjon er deployet enn den fanen kjører. Ukjent
 * deployet versjon gir aldri varsel: et nettverksavbrudd skal ikke få folk
 * til å laste inn siden på nytt.
 */
export function isNewVersionAvailable(current: string, deployed: string | null): boolean {
  return deployed !== null && deployed !== current;
}

/** Siste varsel: hvilken versjon det gjaldt, og når det ble vist. */
export interface LastPrompt {
  version: string;
  at: number;
}

export type UpdatePrompt =
  | { reason: "utrulling"; release: ReleaseNote }
  | { reason: "daglig"; release: null };

/**
 * Om fanen skal be om ny innlasting, og hvorfor.
 *
 * - Ingen ny versjon: aldri.
 * - En utrulling brukeren får se, som fanen ikke har: alltid. `visibleDeployed`
 *   er de deployede utrullingene etter rolle- og flaggfilteret, så en utrulling
 *   bak et flagg som er av, teller ikke før flagget skrus på.
 * - Varsel allerede vist for akkurat denne versjonen: fortsatt synlig, så det
 *   ikke forsvinner av seg selv i fanen der det ble vist.
 * - Ellers: bare når forrige varsel er mer enn et døgn gammelt.
 */
export function updatePrompt(input: {
  current: string;
  deployed: DeployedVersion | null;
  bundleReleaseIds: ReadonlySet<string>;
  visibleDeployed: readonly ReleaseNote[];
  lastPrompt: LastPrompt | null;
  now: number;
}): UpdatePrompt | null {
  const { current, deployed, lastPrompt, now } = input;
  if (!deployed || !isNewVersionAvailable(current, deployed.version)) return null;

  const nyUtrulling = input.visibleDeployed.find((r) => !input.bundleReleaseIds.has(r.id));
  if (nyUtrulling) return { reason: "utrulling", release: nyUtrulling };

  if (lastPrompt?.version === deployed.version) return { reason: "daglig", release: null };
  if (!lastPrompt || now - lastPrompt.at >= DAILY_PROMPT_MS) return { reason: "daglig", release: null };
  return null;
}

/** Leser siste varsel. Null når nettleseren ikke har lagring eller verdien er ugyldig. */
export function readLastPrompt(): LastPrompt | null {
  try {
    const raw = window.localStorage.getItem(LAST_PROMPT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LastPrompt>;
    return typeof parsed.version === "string" && typeof parsed.at === "number"
      ? { version: parsed.version, at: parsed.at }
      : null;
  } catch {
    return null;
  }
}

export function writeLastPrompt(prompt: LastPrompt): void {
  try {
    window.localStorage.setItem(LAST_PROMPT_KEY, JSON.stringify(prompt));
  } catch {
    // Uten lagring vises varselet ved hver deploy, som før.
  }
}
